import InputMask from 'react-input-mask-format';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import { useEffect } from 'react';

import ErrorMessage from 'components/Inputs/components/ErrorMessage';
import { getPersonsDocumentsTypes } from 'pages/dashboard/selectors';
import ErrorIcon from 'components/Inputs/components/ErrorIcon';
import Label from 'components/Inputs/components/Label';
import { CountryISOCodes } from 'common/countries';
import { Input, Select } from 'components/Inputs';
import { useAppSelector } from 'hooks';

import { IdTypes } from '../constants';

interface IdentificationInputProps {
  control: any;
  errors: any;
  disabled: boolean;
  register: any;
  setValue: any;
  watch: any;
  countries: any[];
  idIssuingCountryValue: any;
}

const IdentificationInput: React.FC<IdentificationInputProps> = ({
  control,
  errors,
  disabled,
  register,
  setValue,
  watch,
  countries,
  idIssuingCountryValue,
}) => {
  const { t } = useTranslation('ownership');

  const types = useAppSelector(getPersonsDocumentsTypes);

  const idType = watch('personDocumentsTypesId');
  const countryIsocode = watch('countryIsocode');

  // SSN is a US document, so only offer it when the owner's country is the US.
  const isUS = countryIsocode === CountryISOCodes.USA;

  const availableTypes = isUS
    ? types
    : types.filter((type) => type.description !== IdTypes.SSN);

  useEffect(() => {
    const selectedType = types.find(
      (type) => type.personDocumentsTypesId === idType,
    );

    if (selectedType?.description !== IdTypes.Other) {
      setValue('idTypeName', '');
    }
  }, [types, idType, setValue]);

  useEffect(() => {
    const selectedType = types.find(
      (type) => type.personDocumentsTypesId === idType,
    );

    // When the owner's country changes away from the US, SSN leaves the list —
    // clear the selection so a no-longer-available value isn't kept.
    if (!isUS && selectedType?.description === IdTypes.SSN) {
      setValue('personDocumentsTypesId', '');
    }
  }, [isUS, idType, types, setValue]);

  const options = availableTypes.map((option) => ({
    ...option,
    name: option.description,
    value: option.personDocumentsTypesId,
  }));

  const selectedIdType = options.find((option) => option.value === idType);

  return (
    <div className="w-full mt-6">
      <Select
        label={t('fields.idType.label')}
        placeholder={t('fields.idType.placeholder')}
        disabled={disabled}
        name="personDocumentsTypesId"
        value={selectedIdType}
        className="w-full"
        options={options}
        register={register}
        setValue={setValue}
        error={errors?.personDocumentsTypesId}
      />

      <div className="flex gap-6">
        {selectedIdType?.description === IdTypes.Other && (
          <Input
            name="idTypeName"
            label={t('fields.idTypeName.label')}
            placeholder={t('fields.idTypeName.placeholder')}
            readOnly={disabled}
            className="w-full mt-6"
            register={register}
            error={errors?.idTypeName}
          />
        )}

        {selectedIdType?.description === IdTypes.SSN ? (
          <div className="w-full mt-6">
            <Label>{t('fields.idnumber.label')}</Label>
            <div className="relative flex">
              <Controller
                name="idnumber"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <InputMask
                    mask="999-99-9999"
                    maskPlaceholder={null}
                    value={value}
                    placeholder={t('fields.idnumber.ssnPlaceholder')}
                    onChange={onChange}
                    disabled={disabled}
                    className={classNames(
                      'w-full shadow-sm border focus-visible:outline-none border-gray-300 py-2 px-3 placeholder-gray-400 rounded-md',
                      { 'bg-gray-100': disabled },
                      errors?.idnumber
                        ? 'pr-9 border-red-300 focus:ring-red-500 focus:border-red-500 text-red-900'
                        : 'border-gray-300 focus:ring-0 focus:border-indigo-500',
                    )}
                  />
                )}
              />
              {errors?.idnumber && <ErrorIcon />}
            </div>
            <ErrorMessage error={errors?.idnumber} />
          </div>
        ) : (
          <Input
            name="idnumber"
            label={t('fields.idnumber.label')}
            placeholder={t('fields.idnumber.placeholder')}
            readOnly={disabled}
            className="w-full mt-6"
            register={register}
            error={errors?.idnumber}
          />
        )}
      </div>

      <Select
        label={t('fields.idIssuingCountry.label')}
        placeholder={t('fields.idIssuingCountry.placeholder')}
        disabled={disabled}
        name="idcountryIsocode"
        value={idIssuingCountryValue}
        className="w-full mt-6"
        options={countries}
        register={register}
        setValue={setValue}
        withIcon
        error={errors?.idcountryIsocode}
      />
    </div>
  );
};

export default IdentificationInput;
